import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Drawer,
  Empty,
  Grid,
  Input,
  Pagination,
  Row,
  Select,
  Skeleton,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  EyeOutlined,
  FilterOutlined,
  SearchOutlined,
  ShopOutlined,
  SortAscendingOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { fetchLeads } from '../store/slices/leadsSlice';
import { fetchStores } from '../store/slices/storesSlice';
import { fetchListings } from '../store/slices/listingsSlice';
import PageShell from '../components/Layout/PageShell';
import type { Lead } from '../types';

const { Text } = Typography;
const { RangePicker } = DatePicker;

type SortPreset = 'createdAt:desc' | 'createdAt:asc' | 'status:asc' | 'status:desc';

const SORT_OPTIONS: { value: SortPreset; label: string }[] = [
  { value: 'createdAt:desc', label: 'Newest first' },
  { value: 'createdAt:asc', label: 'Oldest first' },
  { value: 'status:asc', label: 'Status A–Z' },
  { value: 'status:desc', label: 'Status Z–A' },
];

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'converted', label: 'Converted' },
  { value: 'closed', label: 'Closed' },
  { value: 'spam', label: 'Spam' },
];

const INTEREST_LABELS: Record<string, string> = {
  buying: 'Buying',
  renting: 'Renting',
  service: 'Service',
  inquiry: 'Inquiry',
  price_negotiation: 'Price',
  other: 'Other',
};

function leadStatusTag(status: string | undefined, compact?: boolean) {
  if (!status) return <Tag color="default">—</Tag>;
  const label = status.replace(/_/g, ' ').toUpperCase();
  const color =
    status === 'new'
      ? 'blue'
      : status === 'contacted'
        ? 'orange'
        : status === 'in_progress'
          ? 'processing'
          : status === 'converted'
            ? 'success'
            : status === 'closed'
              ? 'default'
              : status === 'spam'
                ? 'red'
                : 'default';
  return (
    <Tag color={color} className={compact ? 'm-0 max-w-full truncate px-1.5 py-0 text-[10px] leading-4' : undefined}>
      {label}
    </Tag>
  );
}

function leadContactName(lead: Lead): string {
  if (lead.guestInfo?.name?.trim()) return lead.guestInfo.name.trim();
  const u = lead.user;
  if (u && typeof u === 'object' && 'profile' in u) {
    const p = (u as { profile?: { firstName?: string; lastName?: string } }).profile;
    const n = `${p?.firstName ?? ''} ${p?.lastName ?? ''}`.trim();
    if (n) return n;
  }
  return 'Unknown contact';
}

function leadContactEmail(lead: Lead): string {
  if (lead.guestInfo?.email?.trim()) return lead.guestInfo.email.trim();
  const u = lead.user;
  if (u && typeof u === 'object' && 'email' in u && typeof (u as { email?: string }).email === 'string') {
    return (u as { email: string }).email;
  }
  return '—';
}

function leadContactPhone(lead: Lead): string {
  if (lead.guestInfo?.phone?.trim()) return lead.guestInfo.phone.trim();
  const u = lead.user;
  if (u && typeof u === 'object' && 'profile' in u) {
    const ph = (u as { profile?: { phone?: string } }).profile?.phone;
    if (ph?.trim()) return ph.trim();
  }
  return '—';
}

function listingTitleFromLead(lead: Lead): string {
  const l = lead.listing;
  if (l && typeof l === 'object' && 'title' in l && typeof (l as { title?: string }).title === 'string') {
    return (l as { title: string }).title;
  }
  return '—';
}

function storeNameFromLead(lead: Lead): string {
  const s = lead.store;
  if (s && typeof s === 'object' && 'storeName' in s && typeof (s as { storeName?: string }).storeName === 'string') {
    return (s as { storeName: string }).storeName;
  }
  return '—';
}

function interestLabel(interestedIn: Lead['interestedIn']): string {
  if (!interestedIn) return '—';
  return INTEREST_LABELS[interestedIn] ?? interestedIn;
}

const Leads: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { leads, loading, pagination } = useAppSelector((state) => state.leads);
  const { stores: vendorStores } = useAppSelector((state) => state.stores);
  const { listings: vendorListings } = useAppSelector((state) => state.listings);

  const screens = Grid.useBreakpoint();
  const useCompactFilters = screens.md === false;
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [storeFilter, setStoreFilter] = useState('');
  const [listingFilter, setListingFilter] = useState('');
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [sortPreset, setSortPreset] = useState<SortPreset>('createdAt:desc');

  useEffect(() => {
    if (!vendorStores.length) {
      dispatch(fetchStores({ page: 1, limit: 100, sortBy: 'name', sortOrder: 'asc' }));
    }
  }, [dispatch, vendorStores.length]);

  useEffect(() => {
    if (!vendorListings.length) {
      dispatch(
        fetchListings({
          page: 1,
          limit: 100,
          sortBy: 'createdAt',
          sortOrder: 'desc',
        })
      );
    }
  }, [dispatch, vendorListings.length]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchText.trim()), 350);
    return () => window.clearTimeout(t);
  }, [searchText]);

  const prevDebouncedRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevDebouncedRef.current === null) {
      prevDebouncedRef.current = debouncedSearch;
      return;
    }
    if (prevDebouncedRef.current !== debouncedSearch) {
      prevDebouncedRef.current = debouncedSearch;
      setPage(1);
    }
  }, [debouncedSearch]);

  const [sortBy, sortOrder] = useMemo(() => {
    const [a, b] = sortPreset.split(':') as ['createdAt' | 'status', 'asc' | 'desc'];
    return [a, b] as const;
  }, [sortPreset]);

  const dateFromIso = dateRange?.[0] ? dateRange[0].startOf('day').toISOString() : undefined;
  const dateToIso = dateRange?.[1] ? dateRange[1].endOf('day').toISOString() : undefined;

  useEffect(() => {
    dispatch(
      fetchLeads({
        page,
        limit: pageSize,
        search: debouncedSearch || undefined,
        status: statusFilter || undefined,
        store: storeFilter || undefined,
        listing: listingFilter || undefined,
        dateFrom: dateFromIso,
        dateTo: dateToIso,
        sortBy,
        sortOrder,
      })
    );
  }, [
    dispatch,
    page,
    pageSize,
    debouncedSearch,
    statusFilter,
    storeFilter,
    listingFilter,
    dateFromIso,
    dateToIso,
    sortBy,
    sortOrder,
  ]);

  const onStatusChange = useCallback((v: string) => {
    setStatusFilter(v);
    setPage(1);
  }, []);

  const onStoreChange = useCallback((v: string) => {
    setStoreFilter(v);
    setListingFilter('');
    setPage(1);
  }, []);

  const onListingChange = useCallback((v: string) => {
    setListingFilter(v);
    setPage(1);
  }, []);

  const onSortChange = useCallback((v: SortPreset) => {
    setSortPreset(v);
    setPage(1);
  }, []);

  const onDateRangeChange = useCallback((values: null | [Dayjs | null, Dayjs | null]) => {
    setDateRange(values);
    setPage(1);
  }, []);

  const onPaginationChange = useCallback(
    (p: number, ps?: number) => {
      const nextSize = ps ?? pageSize;
      if (nextSize !== pageSize) {
        setPageSize(nextSize);
        setPage(1);
      } else {
        setPage(p);
      }
    },
    [pageSize]
  );

  const total = pagination?.totalItems ?? 0;

  const storeFilterOptions = useMemo(
    () =>
      vendorStores.map((s) => ({
        value: s._id,
        label: s.storeName || 'Store',
      })),
    [vendorStores]
  );

  const listingFilterOptions = useMemo(() => {
    const sid = storeFilter || undefined;
    return vendorListings
      .filter((l) => {
        if (!sid) return true;
        const st = l.store;
        const id = typeof st === 'string' ? st : (st as { _id?: string })?._id;
        return id === sid;
      })
      .map((l) => ({
        value: l._id,
        label: l.title || 'Listing',
      }));
  }, [vendorListings, storeFilter]);

  const columns = useMemo(
    () => [
      {
        title: 'Contact',
        key: 'contact',
        width: 260,
        render: (_: unknown, record: Lead) => (
          <div className="min-w-0">
            <div className="truncate font-semibold text-slate-900">{leadContactName(record)}</div>
            <div className="truncate text-xs text-slate-500">{leadContactEmail(record)}</div>
            <div className="truncate text-xs text-slate-500">{leadContactPhone(record)}</div>
          </div>
        ),
      },
      {
        title: 'Listing',
        key: 'listing',
        width: 220,
        render: (_: unknown, record: Lead) => (
          <div className="min-w-0">
            <div className="truncate font-medium text-slate-800">{listingTitleFromLead(record)}</div>
            <div className="truncate text-xs text-slate-500">{storeNameFromLead(record)}</div>
          </div>
        ),
      },
      {
        title: 'Intent',
        key: 'intent',
        width: 110,
        render: (_: unknown, record: Lead) => (
          <Text type="secondary" className="text-xs">
            {interestLabel(record.interestedIn)}
          </Text>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 130,
        render: (status: string) => leadStatusTag(status, false),
      },
      {
        title: 'Received',
        key: 'createdAt',
        width: 120,
        render: (_: unknown, record: Lead) => (
          <span className="whitespace-nowrap text-xs text-slate-600">
            {record.createdAt ? dayjs(record.createdAt).format('MMM D, YYYY') : '—'}
          </span>
        ),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 100,
        render: (_: unknown, record: Lead) => (
          <Button type="link" className="!px-1 font-medium" icon={<EyeOutlined />} onClick={() => navigate(`/leads/${record._id}`)}>
            Open
          </Button>
        ),
      },
    ],
    [navigate]
  );

  const filterActiveCount =
    (statusFilter ? 1 : 0) + (storeFilter ? 1 : 0) + (listingFilter ? 1 : 0) + (dateRange?.[0] && dateRange[1] ? 1 : 0);

  return (
    <PageShell
      title="Leads"
      description="Track enquiries like a pipeline: search contacts or listings, filter by store and status, and open a lead to follow up."
      titleEnd={
        <Button
          type="default"
          icon={<UnorderedListOutlined />}
          onClick={() => navigate('/listings')}
          className="w-full min-[480px]:w-auto border-slate-200 text-slate-800"
        >
          My listings
        </Button>
      }
    >
      <Card className="vendor-card-flush border border-slate-200/80 shadow-sm bg-white rounded-xl overflow-hidden">
        <div className="border-b border-slate-100 bg-slate-50/50 px-3 py-2 sm:px-4">
          {useCompactFilters ? (
            <>
              <div className="flex w-full min-w-0 items-stretch gap-2">
                <Input
                  allowClear
                  size="middle"
                  placeholder="Search name, email, listing…"
                  prefix={<SearchOutlined className="text-slate-400" />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="min-w-0 flex-1 rounded-lg"
                />
                <Badge dot={filterActiveCount > 0} className="shrink-0">
                  <Button
                    type="default"
                    size="middle"
                    aria-label="Open sort and filter"
                    icon={<FilterOutlined />}
                    onClick={() => setFiltersDrawerOpen(true)}
                    className="border-slate-200 text-slate-700"
                  >
                    <span className="hidden min-[380px]:inline">Filters</span>
                  </Button>
                </Badge>
              </div>
              <Drawer
                title="Sort & filter"
                placement="bottom"
                height="auto"
                open={filtersDrawerOpen}
                onClose={() => setFiltersDrawerOpen(false)}
                rootClassName="listings-filter-drawer"
                styles={{ body: { paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' } }}
              >
                <div className="flex flex-col gap-4">
                  <div>
                    <Text type="secondary" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Sort by
                    </Text>
                    <Select
                      size="large"
                      className="w-full"
                      value={sortPreset}
                      onChange={(v) => onSortChange(v as SortPreset)}
                      options={SORT_OPTIONS}
                      showSearch
                      optionFilterProp="label"
                      suffixIcon={<SortAscendingOutlined className="text-slate-500" />}
                    />
                  </div>
                  <div>
                    <Text type="secondary" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Date range
                    </Text>
                    <RangePicker
                      className="w-full"
                      size="large"
                      value={dateRange as [Dayjs, Dayjs] | null}
                      onChange={onDateRangeChange}
                      allowEmpty={[true, true]}
                    />
                  </div>
                  <div>
                    <Text type="secondary" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Status
                    </Text>
                    <Select
                      size="large"
                      className="w-full"
                      placeholder="All statuses"
                      allowClear
                      value={statusFilter || undefined}
                      onChange={(v) => onStatusChange(typeof v === 'string' ? v : '')}
                      options={STATUS_OPTIONS}
                      showSearch
                      optionFilterProp="label"
                    />
                  </div>
                  <div>
                    <Text type="secondary" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Store
                    </Text>
                    <Select
                      size="large"
                      className="w-full"
                      placeholder="All stores"
                      allowClear
                      value={storeFilter || undefined}
                      onChange={(v) => onStoreChange(typeof v === 'string' ? v : '')}
                      options={storeFilterOptions}
                      showSearch
                      optionFilterProp="label"
                      suffixIcon={<ShopOutlined className="text-slate-500" />}
                    />
                  </div>
                  <div>
                    <Text type="secondary" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Listing
                    </Text>
                    <Select
                      size="large"
                      className="w-full"
                      placeholder="All listings"
                      allowClear
                      value={listingFilter || undefined}
                      onChange={(v) => onListingChange(typeof v === 'string' ? v : '')}
                      options={listingFilterOptions}
                      showSearch
                      optionFilterProp="label"
                    />
                  </div>
                  <Button type="primary" block size="large" onClick={() => setFiltersDrawerOpen(false)} className="border-0 bg-gradient-to-r from-[#0171BF] to-[#F7941F] !text-white shadow-md">
                    Done
                  </Button>
                </div>
              </Drawer>
            </>
          ) : (
            <Row gutter={[8, 8]} align="middle">
              <Col xs={24} sm={12} md={7} lg={6} xl={6}>
                <Input
                  allowClear
                  size="middle"
                  placeholder="Search name, email, phone, message, listing title…"
                  prefix={<SearchOutlined className="text-slate-400" />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="rounded-lg"
                />
              </Col>
              <Col xs={24} sm={12} md={9} lg={7} xl={7}>
                <RangePicker
                  className="w-full"
                  size="middle"
                  value={dateRange as [Dayjs, Dayjs] | null}
                  onChange={onDateRangeChange}
                  allowEmpty={[true, true]}
                />
              </Col>
              <Col xs={24} sm={8} md={4} lg={3} xl={3}>
                <Select
                  size="middle"
                  className="w-full"
                  placeholder="Sort"
                  value={sortPreset}
                  onChange={(v) => onSortChange(v as SortPreset)}
                  options={SORT_OPTIONS}
                  showSearch
                  optionFilterProp="label"
                  suffixIcon={<SortAscendingOutlined className="text-slate-500" />}
                />
              </Col>
              <Col xs={24} sm={8} md={4} lg={3} xl={3}>
                <Select
                  size="middle"
                  className="w-full"
                  placeholder="Status"
                  allowClear
                  value={statusFilter || undefined}
                  onChange={(v) => onStatusChange(typeof v === 'string' ? v : '')}
                  options={STATUS_OPTIONS}
                  showSearch
                  optionFilterProp="label"
                />
              </Col>
              <Col xs={24} sm={12} md={8} lg={5} xl={5}>
                <Select
                  size="middle"
                  className="w-full"
                  placeholder="Store"
                  allowClear
                  value={storeFilter || undefined}
                  onChange={(v) => onStoreChange(typeof v === 'string' ? v : '')}
                  options={storeFilterOptions}
                  showSearch
                  optionFilterProp="label"
                  suffixIcon={<ShopOutlined className="text-slate-500" />}
                />
              </Col>
              <Col xs={24} sm={12} md={12} lg={6} xl={6}>
                <Select
                  size="middle"
                  className="w-full"
                  placeholder="Listing"
                  allowClear
                  value={listingFilter || undefined}
                  onChange={(v) => onListingChange(typeof v === 'string' ? v : '')}
                  options={listingFilterOptions}
                  showSearch
                  optionFilterProp="label"
                />
              </Col>
            </Row>
          )}
          <Text type="secondary" className="mt-1.5 block text-[11px] leading-snug sm:text-xs">
            {total === 0 && !loading
              ? 'No leads match your filters.'
              : loading
                ? 'Updating results…'
                : `${total} lead${total === 1 ? '' : 's'} · this page ${leads.length}`}
          </Text>
        </div>

        <div className="hidden lg:block px-2 py-2 sm:px-3">
          <Table<Lead>
            columns={columns}
            dataSource={leads}
            loading={loading}
            rowKey="_id"
            pagination={false}
            size="small"
            className="modern-table"
            scroll={{ x: 1040 }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No enquiries yet — when buyers reach out from your listings, they appear here."
                />
              ),
            }}
          />
        </div>

        <div className="lg:hidden px-2 pb-2 pt-1 sm:px-3 sm:pb-3 sm:pt-2">
          {loading && leads.length === 0 ? (
            <div className="space-y-2.5 py-1">
              {[0, 1, 2].map((i) => (
                <Card key={i} className="rounded-xl border border-slate-200/80 shadow-sm" size="small">
                  <Skeleton active title={{ width: '55%' }} paragraph={{ rows: 3 }} />
                </Card>
              ))}
            </div>
          ) : leads.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No leads match your filters."
              className="py-8"
            />
          ) : (
            <Spin spinning={loading} size="small" className="w-full [&_.ant-spin-nested-loading]:min-h-[4rem]">
              <ul className="m-0 list-none space-y-2.5 p-0 sm:space-y-3">
                {leads.map((lead) => (
                  <li key={lead._id}>
                    <Card
                      size="small"
                      className="overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm transition-shadow hover:shadow-md"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <Text strong className="line-clamp-2 text-sm leading-snug text-slate-900">
                            {leadContactName(lead)}
                          </Text>
                          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                            {leadStatusTag(lead.status, true)}
                            <Text className="text-[11px] font-medium text-slate-600 sm:text-xs">{interestLabel(lead.interestedIn)}</Text>
                          </div>
                          <Text type="secondary" className="mt-1 block text-[11px] leading-snug sm:text-xs">
                            {listingTitleFromLead(lead)}
                            <span className="text-slate-300"> · </span>
                            {storeNameFromLead(lead)}
                          </Text>
                          <Text type="secondary" className="mt-0.5 block text-[11px] leading-snug">
                            {lead.createdAt ? dayjs(lead.createdAt).format('MMM D, YYYY · h:mm A') : ''}
                          </Text>
                        </div>
                      </div>
                      <Divider className="!my-2.5 border-slate-100" />
                      <Button
                        type="primary"
                        size="middle"
                        block
                        icon={<EyeOutlined />}
                        className="min-h-9 border-0 bg-gradient-to-r from-[#0171BF] to-[#F7941F] font-medium !text-white shadow-sm hover:!text-white hover:shadow-md"
                        onClick={() => navigate(`/leads/${lead._id}`)}
                      >
                        Open lead
                      </Button>
                    </Card>
                  </li>
                ))}
              </ul>
            </Spin>
          )}
        </div>

        {total > 0 ? (
          <div className="flex flex-col gap-2 border-t border-slate-100 bg-white px-2 py-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-x-4 sm:gap-y-2 sm:px-3 sm:py-2.5">
            <Text type="secondary" className="block text-center text-xs sm:text-left">
              {`${Math.min((page - 1) * pageSize + 1, total)}–${Math.min(page * pageSize, total)} of ${total}`}
            </Text>
            <Pagination
              current={page}
              pageSize={pageSize}
              total={total}
              showSizeChanger
              pageSizeOptions={[10, 20, 50]}
              onChange={onPaginationChange}
              size="small"
              showTotal={undefined}
              responsive
              className="listings-pagination !m-0 flex w-full justify-center sm:w-auto sm:justify-end text-xs sm:text-sm"
            />
          </div>
        ) : null}
      </Card>
    </PageShell>
  );
};

export default Leads;
